import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';

interface Article {
  _id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  featuredImage?: string;
  author: {
    firstName: string;
    lastName: string;
  };
  views: number;
  publishedAt: string;
}

const Articles: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const { user } = useAuth();
  const canSeeViews = user?.role === 'admin' || user?.role === 'content-creator';

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/articles`, {
        params: { search, category, status: 'published' }
      });
      setArticles(response.data.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (cat: string) => {
    const colors: any = {
      'career-tips': 'bg-blue-100 text-blue-800',
      'tech-news': 'bg-purple-100 text-purple-800',
      'success-stories': 'bg-green-100 text-green-800',
      'fraud-awareness': 'bg-red-100 text-red-800',
      'skills-development': 'bg-orange-100 text-orange-800',
    };
    return colors[cat] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary mb-2">Articles & News</h1>
          <p className="text-gray-600">Career tips, success stories, and industry insights</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              name="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles"
              icon="fas fa-search"
            />

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-field"
            >
              <option value="">All Categories</option>
              <option value="career-tips">Career Tips</option>
              <option value="tech-news">Tech News</option>
              <option value="success-stories">Success Stories</option>
              <option value="fraud-awareness">Fraud Awareness</option>
              <option value="skills-development">Skills Development</option>
            </select>

            <Button variant="primary" onClick={fetchArticles} icon="fas fa-search">
              Search
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
            <p className="mt-4 text-gray-600">Loading articles...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <i className="fas fa-newspaper text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No articles found</h3>
            <p className="text-gray-600">Check back soon for new content</p>
          </div>
        ) : (
          <>
            <div className="mb-4 text-gray-600">
              <i className="fas fa-newspaper mr-2"></i>
              {articles.length} articles found
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Card key={article._id} hover>
                  {article.featuredImage && (
                    <img
                      src={article.featuredImage}
                      alt={article.title}
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                  )}

                  <div className="mb-3">
                    <span className={`inline-block text-xs px-2 py-1 rounded capitalize ${getCategoryColor(article.category)}`}>
                      {article.category.replace('-', ' ')}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-secondary mb-2 line-clamp-2">
                    {article.title}
                  </h3>

                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {article.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                    <div className="flex items-center">
                      <i className="fas fa-user-circle mr-1"></i>
                      <span>{article.author.firstName} {article.author.lastName}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      {canSeeViews && (
                        <span>
                          <i className="fas fa-eye mr-1"></i>
                          {article.views}
                        </span>
                      )}
                      <span>
                        <i className="fas fa-calendar mr-1"></i>
                        {new Date(article.publishedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Button variant="outline" size="sm" fullWidth>
                      Read More
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Articles;
